import { debounce } from 'lodash-es';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

import { useMergedRef, useSubscriptions } from '@/hook';
import { isMacOS } from '@/util';

import { VStack } from '../VStack';
import { joinCn } from '../util';
import { VContextMenuDivider } from './VContextMenuDivider';
import { VContextMenuGroup } from './VContextMenuGroup';
import { VContextMenuItem } from './VContextMenuItem';
import { VContextMenuContext } from './context';
import twStyle from './style';
import type { VContextMenuProps } from './type';

const IS_MACOS = isMacOS();
const DEFAULT_DURATION = 250;

const MotionVStack = motion.create(VStack, { forwardMotionProps: true });

export const VContextMenu = ({
  children,
  className,
  duration = DEFAULT_DURATION,
  position,
  ref,
  tabIndex,
  visible = false,
  onUpdateVisible,
}: React.PropsWithChildren<VContextMenuProps> &
  React.ComponentPropsWithRef<'div'>) => {
  const [innerVisible, setInnerVisible] = useState(visible);
  const $containerRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergedRef($containerRef, ref);
  const innerVisibleRef = useRef(visible);
  const style = twStyle({});

  useSubscriptions(() => [
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter(
          e =>
            e.key === 'Escape' &&
            !!innerVisibleRef.current &&
            !!$containerRef.current &&
            document.activeElement === $containerRef.current,
        ),
      )
      .subscribe(() => setInnerVisible(false)),
  ]);

  useSubscriptions(() => [
    fromEvent<MouseEvent>(document, 'mousedown')
      .pipe(
        filter(e => {
          if (!innerVisibleRef.current || !$containerRef.current) return false;
          return !$containerRef.current.contains(e.target as Node);
        }),
      )
      .subscribe(() => setInnerVisible(false)),
  ]);

  useEffect(() => {
    if (innerVisible && $containerRef.current) {
      debounce(() => $containerRef.current?.focus(), duration)();
    }
  }, [duration, innerVisible]);

  useEffect(() => {
    if (visible === innerVisible) return;

    setInnerVisible(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    innerVisibleRef.current = innerVisible;

    if (visible === innerVisible) return;

    onUpdateVisible?.(innerVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerVisible]);

  return (
    <VContextMenuContext.Provider
      value={{
        duration,
        isMacOS: IS_MACOS,
        position,
        style,
        visible,
        onUpdateVisible,
      }}
    >
      <AnimatePresence>
        {innerVisible && (
          <MotionVStack
            ref={mergedRef}
            aria-label="v-context-menu"
            className={joinCn(style.container, className)}
            gap={0.5}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="menu"
            style={{ left: position?.x, top: position?.y }}
            tabIndex={tabIndex ?? 0}
            transition={{ duration: duration / 1000 }}
          >
            {children}
          </MotionVStack>
        )}
      </AnimatePresence>
    </VContextMenuContext.Provider>
  );
};

VContextMenu.displayName = 'VContextMenu';

VContextMenu.Divider = VContextMenuDivider;
VContextMenu.Group = VContextMenuGroup;
VContextMenu.Item = VContextMenuItem;
